import { useState, useCallback } from 'react';
export * from '../utils/customSectionUtils';

export interface SitemapNode {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  status: 'default' | 'optional';
  children?: SitemapNode[];
}

export interface PageContent {
  pageId: string;
  pageName: string;
  status: 'Generated' | 'Pending' | 'Edited';
  sections: ContentSection[];
}

export type SectionType = 'hero' | 'usp' | 'story' | 'grid' | 'gallery' | 'blog' | 'contact' | 'founder' | 'team' | 'problemSolution' | 'serviceCard' | 'productCard' | 'testimonial' | 'clientLogos' | 'faq' | 'contactInfo' | 'contactForm' | 'custom' | 'customSection';

export interface ContentSection {
  id: string;
  title: string;
  type: SectionType;
  content: string | string[] | ContentCard[];
  editing: boolean;
  enabled?: boolean;
  subtitle?: string;
  description?: string;
  heading?: string;
}

export interface ContentCard {
  title: string;
  description: string;
  icon?: string;
}

export interface BlogPreview {
  title: string;
  excerpt: string;
  date: string;
  content: string;
}

export interface Template {
  id: string;
  name: string;
  category: string;
  thumbnail: string;
  description: string;
  headingFont?: string;
  bodyFont?: string;
}

export interface ThemeSettings {
  primary: string;
  accent: string;
  background: string;
  text: string;
  cta: string;
  headingFont: string;
  bodyFont: string;
}

export interface SEOField {
  pageId: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  urlSlug: string;
}

export interface AlertRule {
  id: string;
  label: string;
  enabled: boolean;
  threshold: string;
}

export interface SitemapPage {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  status: 'default' | 'optional';
}

export interface ContentSectionData {
  id: string;
  title: string;
  type: SectionType;
  content: string | string[] | SitemapPage[] | BlogPreview[];
  editing: boolean;
  subtitle?: string;
  description?: string;
  heading?: string;
}

export const sitemapData: SitemapNode[] = [
  {
    id: 'home',
    name: 'Home',
    enabled: true,
    description: 'Main landing page with hero, USP strip, services, gallery, and contact sections.',
    status: 'default',
    children: [],
  },
  {
    id: 'about',
    name: 'About Us',
    enabled: true,
    description: 'Brand story, mission, team, and company values.',
    status: 'default',
  },
  {
    id: 'services',
    name: 'Services',
    enabled: true,
    description: 'Service listings with descriptions, pricing, and booking CTA.',
    status: 'default',
    children: [],
  },
  {
    id: 'products',
    name: 'Products',
    enabled: true,
    description: 'Product catalog with descriptions, pricing, and buy now CTAs.',
    status: 'default',
    children: [],
  },
  {
    id: 'blog',
    name: 'Blog',
    enabled: true,
    description: 'Articles, tips, and thought leadership content.',
    status: 'default',
  },
  {
    id: 'gallery',
    name: 'Gallery',
    enabled: true,
    description: 'Image gallery showcasing portfolio and work.',
    status: 'default',
  },
  {
    id: 'contact',
    name: 'Contact Us',
    enabled: true,
    description: 'Contact form, map, phone, email, and hours.',
    status: 'default',
  }
      ];

export const pageContents: PageContent[] = [
  {
    pageId: 'home',
    pageName: 'Home',
    status: 'Pending',
    sections: [
      { id: 'hero1', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'usp1', title: 'Brand USP Strip', type: 'usp', content: [], editing: false, enabled: true },
      { id: 'story1', title: 'Brand Story Snippet', type: 'story', content: '', editing: false, enabled: true },
      { id: 'grid-services', title: 'Services We Offer', type: 'grid', content: [], editing: false, enabled: true },
      { id: 'grid-products', title: 'Products We Offer', type: 'grid', content: [], editing: false, enabled: true },
      { id: 'gallery1', title: 'Gallery Strip', type: 'gallery', content: [], editing: false, enabled: true },
      { id: 'blog1', title: 'Top 3 Blogs', type: 'blog', content: [], editing: false, enabled: true },
    ],
  },
  {
    pageId: 'about',
    pageName: 'About Us',
    status: 'Pending',
    sections: [
      { id: 'about-hero', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'about-mission', title: 'Mission', type: 'story', content: '', editing: false, enabled: true },
      { id: 'about-vision', title: 'Vision', type: 'story', content: '', editing: false, enabled: true },
      { id: 'about-brand-story', title: 'Brand Story', type: 'story', content: '', editing: false, enabled: true },
      { id: 'about-founder', title: 'Founder', type: 'founder', content: [], editing: false, enabled: true },
      { id: 'about-team', title: 'Team', type: 'team', content: [], editing: false, enabled: true },
    ],
  },
  {
    pageId: 'services',
    pageName: 'Services',
    status: 'Pending',
    sections: [
      { id: 'sl-hero', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'sl-problem', title: 'Problem-Solution', type: 'problemSolution', content: [], editing: false, enabled: true },
      { id: 'sl-services', title: 'Our Services', type: 'serviceCard', content: [], editing: false, enabled: true },
      { id: 'sl-usp', title: 'Why Choose Us', type: 'usp', content: [], editing: false, enabled: true },
      { id: 'sl-testimonials', title: 'Client Testimonials', type: 'testimonial', content: [], editing: false, enabled: true },
      { id: 'sl-clients', title: 'Our Clients', type: 'clientLogos', content: [], editing: false, enabled: true },
      { id: 'sl-faq', title: 'Frequently Asked Questions', type: 'faq', content: [], editing: false, enabled: true },
    ],
  },
  {
    pageId: 'products',
    pageName: 'Products',
    status: 'Pending',
    sections: [
      { id: 'pl-hero', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'pl-problem', title: 'Problem-Solution', type: 'problemSolution', content: [], editing: false, enabled: true },
      { id: 'pl-products', title: 'Our Products', type: 'productCard', content: [], editing: false, enabled: true },
      { id: 'pl-testimonials', title: 'Client Testimonials', type: 'testimonial', content: [], editing: false, enabled: true },
      { id: 'pl-clients', title: 'Our Clients', type: 'clientLogos', content: [], editing: false, enabled: true },
      { id: 'pl-faq', title: 'Frequently Asked Questions', type: 'faq', content: [], editing: false, enabled: true },
    ],
  },
  {
    pageId: 'blog',
    pageName: 'Blog',
    status: 'Pending',
    sections: [
      { id: 'blog-hero', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'blog-posts', title: 'Latest Articles', type: 'blog', content: [], editing: false, enabled: true },
    ],
  },
  {
    pageId: 'gallery',
    pageName: 'Gallery',
    status: 'Pending',
    sections: [
      { id: 'gallery-hero', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'gallery-main', title: 'Portfolio Gallery', type: 'gallery', content: [], editing: false, enabled: true },
    ],
  },
  {
    pageId: 'contact',
    pageName: 'Contact Us',
    status: 'Pending',
    sections: [
      { id: 'contact-hero', title: 'Hero Banner', type: 'hero', content: [], editing: false, enabled: true },
      { id: 'contactInfo-contact', title: 'Contact Information', type: 'contactInfo', content: '', editing: false, enabled: true },
      { id: 'contactForm-contact', title: 'Contact Form', type: 'contactForm', content: '{"heading": "Contact Us", "subtext": "Fill out the form below and our team will get back to you as soon as possible."}', editing: false, enabled: true },
    ],
  },
];

export const templates: Template[] = [
  { id: 't1', name: 'Minimal Studio', category: 'General', headingFont: 'Inter', bodyFont: 'Inter', thumbnail: 'https://readdy.ai/api/search-image?query=Minimalist%20website%20homepage%20mockup%20on%20laptop%20screen%2C%20clean%20white%20background%2C%20elegant%20typography%2C%20cream%20and%20charcoal%20color%20scheme%2C%20editorial%20web%20design%20preview%2C%20soft%20shadows&width=400&height=280&seq=t1&orientation=landscape', description: 'Clean, spacious layout with generous whitespace and refined typography.' },
  { id: 't2', name: 'Bistro Table', category: 'Restaurant', headingFont: 'Playfair Display', bodyFont: 'Georgia', thumbnail: 'https://readdy.ai/api/search-image?query=Restaurant%20website%20homepage%20mockup%20on%20laptop%2C%20warm%20food%20photography%2C%20elegant%20menu%20layout%2C%20cream%20and%20gold%20tones%2C%20editorial%20web%20design%20preview%2C%20appetizing%20hero%20image&width=400&height=280&seq=t2&orientation=landscape', description: 'Warm, inviting design optimized for menus, reservations, and food galleries.' },
  { id: 't3', name: 'Glamour Chair', category: 'Salon', headingFont: 'DM Serif Display', bodyFont: 'DM Sans', thumbnail: 'https://readdy.ai/api/search-image?query=Beauty%20salon%20website%20homepage%20mockup%20on%20laptop%2C%20elegant%20feminine%20design%2C%20soft%20pink%20and%20cream%20tones%2C%20services%20grid%20layout%2C%20editorial%20web%20design%20preview%2C%20luxury%20aesthetic&width=400&height=280&seq=t3&orientation=landscape', description: 'Sophisticated, feminine design with appointment booking and service showcases.' },
  { id: 't4', name: 'Healing Touch', category: 'Clinic', headingFont: 'Helvetica Neue', bodyFont: 'Arial', thumbnail: 'https://readdy.ai/api/search-image?query=Medical%20clinic%20website%20homepage%20mockup%20on%20laptop%2C%20clean%20professional%20design%2C%20soft%20blue%20and%20white%20tones%2C%20trust-building%20layout%2C%20editorial%20web%20design%20preview%2C%20modern%20healthcare%20aesthetic&width=400&height=280&seq=t4&orientation=landscape', description: 'Trust-building, clinical design with appointment scheduling and doctor profiles.' },
  { id: 't5', name: 'Shelf & Cart', category: 'Retail', headingFont: 'DM Serif Display', bodyFont: 'DM Sans', thumbnail: 'https://readdy.ai/api/search-image?query=E-commerce%20retail%20website%20homepage%20mockup%20on%20laptop%2C%20product%20grid%20layout%2C%20clean%20modern%20design%2C%20cream%20and%20charcoal%20tones%2C%20editorial%20web%20design%20preview%2C%20shopping%20aesthetic&width=400&height=280&seq=t5&orientation=landscape', description: 'Product-focused design with grid layouts, quick-view, and seamless checkout.' },
  { id: 't6', name: 'Boardroom', category: 'Professional', headingFont: 'Georgia', bodyFont: 'Helvetica Neue', thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20corporate%20website%20homepage%20mockup%20on%20laptop%2C%20dark%20navy%20and%20gold%20color%20scheme%2C%20elegant%20serif%20typography%2C%20editorial%20web%20design%20preview%2C%20luxury%20business%20aesthetic%2C%20clean%20lines&width=400&height=280&seq=t6&orientation=landscape', description: 'Authoritative, premium design for consultancies, law firms, and agencies.' }
      ];

export const defaultTheme: ThemeSettings = {
  primary: '#1b2240',
  accent: '#c8a15a',
  background: '#faf8f5',
  text: '#1a1d2e',
  cta: '#c8a15a',
  headingFont: 'DM Serif Display',
  bodyFont: 'DM Sans',
};

export const seoFields: SEOField[] = [
  { pageId: 'home', h1: 'AI-Powered Marketing Intelligence for Founders', metaTitle: 'Marketing 4Sight | AI SEO & Growth Platform for Startups', metaDescription: 'Transform your marketing with AI-driven insights. Local SEO, content strategy, and GMB management — all in one platform.', urlSlug: 'home' },
  { pageId: 'about', h1: 'About Marketing 4Sight', metaTitle: 'About Us | Marketing 4Sight', metaDescription: 'Learn how Marketing 4Sight is democratizing enterprise-grade marketing intelligence for founders worldwide.', urlSlug: 'about-us' },
  { pageId: 'services', h1: 'Our Services', metaTitle: 'Services | Marketing 4Sight', metaDescription: 'Explore our suite of AI-powered marketing services designed to accelerate your growth.', urlSlug: 'services' },
  { pageId: 'products', h1: 'Our Products', metaTitle: 'Products | Marketing 4Sight', metaDescription: 'Discover tools and platforms built to scale your marketing operations effortlessly.', urlSlug: 'products' },
  { pageId: 'blog', h1: 'Insights & Strategies', metaTitle: 'Blog | Marketing 4Sight', metaDescription: 'Expert articles, growth strategies, and industry insights for modern marketers.', urlSlug: 'blog' },
  { pageId: 'gallery', h1: 'Our Work', metaTitle: 'Gallery | Marketing 4Sight', metaDescription: 'Browse our portfolio of successful marketing campaigns and client results.', urlSlug: 'gallery' },
  { pageId: 'contact', h1: 'Get in Touch', metaTitle: 'Contact | Marketing 4Sight', metaDescription: 'Have questions? Reach out to our team and we will get back to you within 24 hours.', urlSlug: 'contact-us' }
      ];

export const alertRules: AlertRule[] = [
  { id: 'rating', label: 'Rating drops below', enabled: true, threshold: '4.0' },
  { id: 'post', label: 'Post not published for', enabled: true, threshold: '7' },
  { id: 'review', label: 'Review unanswered', enabled: true, threshold: '24h' },
  { id: 'edit', label: 'Google-suggested edit received', enabled: false, threshold: '' }
      ];

export interface SearchTerm {
  term: string;
  impressions: number;
  ctr: string;
  inDescription: boolean;
  inPosts: boolean;
  opportunity: 'high' | 'medium' | 'low';
}

export interface TeamMember {
  name: string;
  role: string;
  canView: boolean;
  canEdit: boolean;
  canPublish: boolean;
  canRevoke: boolean;
}

export interface PerformanceMetric {
  name: string;
  value: number;
  change: number;
  changeType: 'positive' | 'negative' | 'neutral';
}

export interface HealthCheckItem {
  label: string;
  status: 'pass' | 'fail' | 'warn';
  action?: string;
}

export interface ActivityLogEntry {
  action: string;
  actor: string;
  section: string;
  timestamp: string;
}

export interface ActiveAlert {
  type: 'error' | 'warning' | 'info' | 'success';
  message: string;
  cta?: string;
  ctaLink?: string;
}

export const performanceMetrics: PerformanceMetric[] = [
  { name: 'Profile Impressions', value: 1240, change: 15, changeType: 'positive' },
  { name: 'Website Clicks', value: 340, change: 12, changeType: 'positive' },
  { name: 'Calls', value: 38, change: 5, changeType: 'positive' },
  { name: 'Directions', value: 22, change: -3, changeType: 'negative' },
  { name: 'Messages', value: 9, change: 2, changeType: 'positive' },
  { name: 'Bookings', value: 7, change: 40, changeType: 'positive' }
      ];

export const searchTerms: SearchTerm[] = [
  { term: 'marketing agency near me', impressions: 452, ctr: '8.2%', inDescription: true, inPosts: true, opportunity: 'low' },
  { term: 'local seo services', impressions: 389, ctr: '6.1%', inDescription: true, inPosts: false, opportunity: 'medium' },
  { term: 'ai marketing tools', impressions: 312, ctr: '5.4%', inDescription: false, inPosts: true, opportunity: 'medium' },
  { term: 'startup growth strategy', impressions: 278, ctr: '4.9%', inDescription: false, inPosts: false, opportunity: 'high' },
  { term: 'content marketing platform', impressions: 198, ctr: '3.8%', inDescription: true, inPosts: false, opportunity: 'medium' }
      ];

export const teamMembers: TeamMember[] = [
  { name: 'AK', role: 'Admin', canView: true, canEdit: true, canPublish: true, canRevoke: true },
  { name: 'Priya', role: 'Manager', canView: true, canEdit: true, canPublish: true, canRevoke: false },
  { name: 'Rahul', role: 'Viewer', canView: true, canEdit: false, canPublish: false, canRevoke: false }
      ];

export const healthChecklist: HealthCheckItem[] = [
  { label: 'Profile photo', status: 'pass' },
  { label: 'Cover image', status: 'pass' },
  { label: 'Description', status: 'pass' },
  { label: 'Hours', status: 'pass' },
  { label: 'Photos', status: 'fail', action: 'Upload 8 more' },
  { label: 'Posts', status: 'fail', action: 'Schedule 2 posts' }
      ];

export const activityLog: ActivityLogEntry[] = [
  { action: 'Description updated', actor: 'AK', section: 'Overview', timestamp: '10:32 AM' },
  { action: 'Photo uploaded', actor: 'AK', section: 'Photos', timestamp: '9:15 AM' },
  { action: 'Post scheduled', actor: 'Priya', section: 'Posts', timestamp: 'Yesterday' },
  { action: 'Goal edited', actor: 'AK', section: 'Goals', timestamp: 'Yesterday' },
  { action: 'Review responded', actor: 'Priya', section: 'Reviews', timestamp: 'May 6' },
  { action: 'Category changed', actor: 'AK', section: 'Info', timestamp: 'May 5' },
  { action: 'Hours updated', actor: 'Rahul', section: 'Info', timestamp: 'May 4' },
  { action: 'Team member added', actor: 'AK', section: 'Team', timestamp: 'May 3' }
      ];

export const activeAlerts: ActiveAlert[] = [
  { type: 'error', message: '3 reviews unanswered', cta: 'Go to Review Plan', ctaLink: '#' },
  { type: 'warning', message: 'Post expiring in 2 days', cta: 'Post Scheduler', ctaLink: '#' },
  { type: 'info', message: 'Hours not updated', ctaLink: '#' },
  { type: 'success', message: 'Google suggested category edit', ctaLink: '#' }
      ];

export const fonts = [
  'DM Serif Display',
  'DM Sans',
  'Inter',
  'Playfair Display',
  'Figtree',
  'Georgia',
  'Helvetica Neue',
  'Arial'
      ];

export interface SiteNode {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  status: 'default' | 'optional';
  children?: SiteNode[];
}

export const siteMapNodes: SiteNode[] = [
  {
    id: 'home',
    name: 'Home',
    enabled: true,
    description: 'Main landing page with hero, USP strip, services, gallery, and contact sections.',
    status: 'default',
  },
  {
    id: 'about',
    name: 'About Us',
    enabled: true,
    description: 'Brand story, mission, team, and company values.',
    status: 'default',
  },
  {
    id: 'services',
    name: 'Services',
    enabled: true,
    description: 'Service listings with descriptions, pricing, and booking CTA.',
    status: 'default',
    children: [],
  },
  {
    id: 'products',
    name: 'Products',
    enabled: true,
    description: 'Product catalog with descriptions, pricing, and buy now CTAs.',
    status: 'default',
    children: [],
  },
  {
    id: 'blog',
    name: 'Blog',
    enabled: true,
    description: 'Articles, tips, and thought leadership content.',
    status: 'default',
  },
  {
    id: 'gallery',
    name: 'Gallery',
    enabled: true,
    description: 'Image gallery showcasing portfolio and work.',
    status: 'default',
  },
  {
    id: 'contact',
    name: 'Contact Us',
    enabled: true,
    description: 'Contact form, map, phone, email, and hours.',
    status: 'default',
  }
      ];

export interface TemplateData {
  id: string;
  name: string;
  category: string;
  thumbnail: string;
  description: string;
  headingFont?: string;
  bodyFont?: string;
  palette?: { primary: string; accent: string; background: string; text: string; cta: string; };
}

export interface ThemeData {
  primary: string;
  accent: string;
  background: string;
  text: string;
  cta: string;
  headingFont: string;
  bodyFont: string;
}

export interface SEOData {
  pageId: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  urlSlug: string;
}

export const templateList: TemplateData[] = [
  { id: 't1', name: 'Minimal Studio', category: 'General', headingFont: 'Inter', bodyFont: 'Inter', thumbnail: 'https://readdy.ai/api/search-image?query=Minimalist%20website%20homepage%20mockup%20on%20laptop%20screen%2C%20clean%20white%20background%2C%20elegant%20typography%2C%20cream%20and%20charcoal%20color%20scheme%2C%20editorial%20web%20design%20preview%2C%20soft%20shadows&width=400&height=280&seq=t1&orientation=landscape', description: 'Clean, spacious layout with generous whitespace and refined typography.', palette: { primary: '#111111', accent: '#666666', background: '#fdfdfc', text: '#111111', cta: '#111111' } },
  { id: 't2', name: 'Bistro Table', category: 'Restaurant', headingFont: 'Playfair Display', bodyFont: 'Georgia', thumbnail: 'https://readdy.ai/api/search-image?query=Restaurant%20website%20homepage%20mockup%20on%20laptop%2C%20warm%20food%20photography%2C%20elegant%20menu%20layout%2C%20cream%20and%20gold%20tones%2C%20editorial%20web%20design%20preview%2C%20appetizing%20hero%20image&width=400&height=280&seq=t2&orientation=landscape', description: 'Warm, inviting design optimized for menus, reservations, and food galleries.', palette: { primary: '#4a5d23', accent: '#c8a15a', background: '#faf8f5', text: '#2c3e21', cta: '#c8a15a' } },
  { id: 't3', name: 'Glamour Chair', category: 'Salon', headingFont: 'DM Serif Display', bodyFont: 'DM Sans', thumbnail: 'https://readdy.ai/api/search-image?query=Beauty%20salon%20website%20homepage%20mockup%20on%20laptop%2C%20elegant%20feminine%20design%2C%20soft%20pink%20and%20cream%20tones%2C%20services%20grid%20layout%2C%20editorial%20web%20design%20preview%2C%20luxury%20aesthetic&width=400&height=280&seq=t3&orientation=landscape', description: 'Sophisticated, feminine design with appointment booking and service showcases.', palette: { primary: '#b87b7b', accent: '#e8c9c9', background: '#fdfbfb', text: '#4a4a4a', cta: '#b87b7b' } },
  { id: 't4', name: 'Healing Touch', category: 'Clinic', headingFont: 'Helvetica Neue', bodyFont: 'Arial', thumbnail: 'https://readdy.ai/api/search-image?query=Medical%20clinic%20website%20homepage%20mockup%20on%20laptop%2C%20clean%20professional%20design%2C%20soft%20blue%20and%20white%20tones%2C%20trust-building%20layout%2C%20editorial%20web%20design%20preview%2C%20modern%20healthcare%20aesthetic&width=400&height=280&seq=t4&orientation=landscape', description: 'Trust-building, clinical design with appointment scheduling and doctor profiles.', palette: { primary: '#2a6b8f', accent: '#8dc6ff', background: '#f0f4f8', text: '#1e3a4c', cta: '#2a6b8f' } },
  { id: 't5', name: 'Shelf & Cart', category: 'Retail', headingFont: 'DM Serif Display', bodyFont: 'DM Sans', thumbnail: 'https://readdy.ai/api/search-image?query=E-commerce%20retail%20website%20homepage%20mockup%20on%20laptop%2C%20product%20grid%20layout%2C%20clean%20modern%20design%2C%20cream%20and%20charcoal%20tones%2C%20editorial%20web%20design%20preview%2C%20shopping%20aesthetic&width=400&height=280&seq=t5&orientation=landscape', description: 'Product-focused design with grid layouts, quick-view, and seamless checkout.', palette: { primary: '#2b2b2b', accent: '#a6a6a6', background: '#f9f9f9', text: '#1a1a1a', cta: '#2b2b2b' } },
  { id: 't6', name: 'Boardroom', category: 'Professional', headingFont: 'Georgia', bodyFont: 'Helvetica Neue', thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20corporate%20website%20homepage%20mockup%20on%20laptop%2C%20dark%20navy%20and%20gold%20color%20scheme%2C%20elegant%20serif%20typography%2C%20editorial%20web%20design%20preview%2C%20luxury%20business%20aesthetic%2C%20clean%20lines&width=400&height=280&seq=t6&orientation=landscape', description: 'Authoritative, premium design for consultancies, law firms, and agencies.', palette: { primary: '#1b2240', accent: '#c8a15a', background: '#faf8f5', text: '#1a1d2e', cta: '#c8a15a' } }
      ];

export const initialTheme: ThemeData = {
  primary: '#1b2240',
  accent: '#c8a15a',
  background: '#faf8f5',
  text: '#1a1d2e',
  cta: '#c8a15a',
  headingFont: 'DM Serif Display',
  bodyFont: 'DM Sans',
};

export const seoDataList: SEOData[] = [
  { pageId: 'home', h1: 'AI-Powered Marketing Intelligence for Founders', metaTitle: 'Marketing 4Sight | AI SEO & Growth Platform for Startups', metaDescription: 'Transform your marketing with AI-driven insights. Local SEO, content strategy, and GMB management — all in one platform.', urlSlug: 'home' },
  { pageId: 'about', h1: 'About Marketing 4Sight', metaTitle: 'About Us | Marketing 4Sight', metaDescription: 'Learn how Marketing 4Sight is democratizing enterprise-grade marketing intelligence for founders worldwide.', urlSlug: 'about-us' },
  { pageId: 'services', h1: 'Our Services', metaTitle: 'Services | Marketing 4Sight', metaDescription: 'Explore our suite of AI-powered marketing services designed to accelerate your growth.', urlSlug: 'services' },
  { pageId: 'products', h1: 'Our Products', metaTitle: 'Products | Marketing 4Sight', metaDescription: 'Discover tools and platforms built to scale your marketing operations effortlessly.', urlSlug: 'products' },
  { pageId: 'blog', h1: 'Insights & Strategies', metaTitle: 'Blog | Marketing 4Sight', metaDescription: 'Expert articles, growth strategies, and industry insights for modern marketers.', urlSlug: 'blog' },
  { pageId: 'gallery', h1: 'Our Work', metaTitle: 'Gallery | Marketing 4Sight', metaDescription: 'Browse our portfolio of successful marketing campaigns and client results.', urlSlug: 'gallery' },
  { pageId: 'contact', h1: 'Get in Touch', metaTitle: 'Contact | Marketing 4Sight', metaDescription: 'Have questions? Reach out to our team and we will get back to you within 24 hours.', urlSlug: 'contact-us' }
      ];

export const getOnboardingTheme = (): ThemeData => {
  // Simulates values extracted from the Brand Onboarding → Business Portfolio step
  // In production this would read from onboarding state / backend
  return {
    primary: '#54B8A6',
    accent: '#8D94BB',
    background: '#faf8f5',
    text: '#1a1d2e',
    cta: '#54B8A6',
    headingFont: 'Playfair Display',
    bodyFont: 'Inter',
  };
};

export const fontOptions = [
  'DM Serif Display',
  'DM Sans',
  'Inter',
  'Playfair Display',
  'Figtree',
  'Georgia',
  'Helvetica Neue',
  'Arial'
      ];

export const performanceChartData = [
  { week: 'Week 1', impressions: 980, clicks: 280, calls: 32, directions: 18 },
  { week: 'Week 2', impressions: 1050, clicks: 310, calls: 35, directions: 20 },
  { week: 'Week 3', impressions: 1180, clicks: 325, calls: 36, directions: 24 },
  { week: 'Week 4', impressions: 1240, clicks: 340, calls: 38, directions: 22 }
      ];

export const healthHistory = [
  { month: 'Dec', score: 58 },
  { month: 'Jan', score: 62 },
  { month: 'Feb', score: 65 },
  { month: 'Mar', score: 66 },
  { month: 'Apr', score: 68 },
  { month: 'May', score: 72 }
      ];

export const adReadiness = [
  { label: 'Profile complete', met: true },
  { label: 'Rating ≥ 4.5', met: true },
  { label: 'Hours updated', met: true },
  { label: 'Photos ≥ 20', met: false, action: 'Fix now →' }
      ];

export const heatmapData = [
  { day: 'Mon', hours: [2, 3, 4, 5, 6, 7, 8, 9, 7, 5, 4, 3, 2, 1] },
  { day: 'Tue', hours: [3, 4, 5, 6, 7, 8, 9, 8, 7, 6, 5, 4, 3, 2] },
  { day: 'Wed', hours: [4, 5, 6, 7, 8, 9, 10, 9, 8, 7, 6, 5, 4, 3] },
  { day: 'Thu', hours: [3, 4, 5, 6, 7, 8, 9, 10, 9, 8, 7, 6, 5, 4] },
  { day: 'Fri', hours: [5, 6, 7, 8, 9, 10, 11, 10, 9, 8, 7, 6, 5, 4] },
  { day: 'Sat', hours: [7, 8, 9, 10, 11, 12, 12, 11, 10, 9, 8, 7, 6, 5] },
  { day: 'Sun', hours: [6, 7, 8, 9, 10, 11, 10, 9, 8, 7, 6, 5, 4, 3] }
      ];

export const actionSuggestions = [
  { message: 'Haven\'t posted in 14 days', priority: 'high', action: 'Create Post' },
  { message: '3 reviews unanswered', priority: 'high', action: 'Respond' },
  { message: 'Direction requests dropped — check address', priority: 'medium', action: 'Verify' },
  { message: 'Upload 5 more photos to match competitors', priority: 'medium', action: 'Upload' }
      ];

export const monthlyAuditNeeds = [
  { label: 'Photos below target', current: 12, target: 20, status: 'fail' as const },
  { label: 'Posting frequency dropped', current: 2, target: 4, status: 'warn' as const },
  { label: 'Secondary categories not set', current: 1, target: 3, status: 'warn' as const }
      ];

export const monthlyAuditPassing = [
  { label: 'Description complete', status: 'pass' as const },
  { label: 'NAP consistent', status: 'pass' as const },
  { label: 'Reviews responded within 24h', status: 'pass' as const },
  { label: 'Hours up to date', status: 'pass' as const }
      ];

export const adTypeCards = [
  { title: 'Local Search Ads', description: 'Appear at the top of Google Maps and local search results when customers are actively looking for services like yours.' },
  { title: 'Promoted Map Pins', description: 'Stand out on Google Maps with a distinctive, highlighted pin that draws attention to your location.' },
  { title: 'Click-to-Call Campaigns', description: 'Drive direct phone calls from mobile users with one-tap calling buttons in your search ads.' }
      ];

export const useSitemapState = () => {
  const [nodes, setNodes] = useState<SiteNode[]>(siteMapNodes);

  const toggleNode = useCallback((nodeId: string) => {
    setNodes(prev => prev.map(node => {
      if (node.id === nodeId) {
        return { ...node, enabled: !node.enabled };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(child =>
            child.id === nodeId ? { ...child, enabled: !child.enabled } : child
          ),
        };
      }
      return node;
    }));
  }, []);

  return { nodes, toggleNode };
};